import React, { useState, useRef } from 'react';
import { useEffect } from 'react';
import {
  Briefcase,
  User,
  Calendar,
  Clock,
  Bot,
  BookOpen,
  Settings,
  Sun,
  Moon,
  LogOut,
  Image as ImageIcon,
  MessageSquare,
  Mail,
  ChevronDown,
  ChevronUp,
  Trash2
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { supabase } from '../../lib/supabaseClient';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NotificationBell from '../../components/consultant/NotificationBell';

interface DashboardCard {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  count?: number;
  status?: string;
  href: string;
}

interface AdminMessage {
  id: string;
  subject: string;
  message: string;
  read_by_consultant: boolean;
  created_at: string;
}

export default function ConsultantDashboard() {
  const { theme, toggleTheme, setTheme, setThemeTransient } = useTheme();
  const navigate = useNavigate();
  const { user, loading, signOut } = useAuth();

  // Apply light theme synchronously before first render to avoid dark flash on Safari
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', 'light');
  }
  const [consultantProfile, setConsultantProfile] = useState<any>(null);
  const [activeAssignment, setActiveAssignment] = useState<any>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoadingLogo, setIsLoadingLogo] = useState(true);
  const [opportunitiesCount, setOpportunitiesCount] = useState<number>(0);
  const [assignmentsCount, setAssignmentsCount] = useState<number>(0);
  const [profileCompleteness, setProfileCompleteness] = useState<number>(0);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [adminMessages, setAdminMessages] = useState<AdminMessage[]>([]);
  const [messagesVisible, setMessagesVisible] = useState<boolean>(() => {
    try { return localStorage.getItem('consultant_msgs_visible') !== 'false'; } catch { return true; }
  });
  const [expandedMessageId, setExpandedMessageId] = useState<string | null>(null);
  const [swipeOffsets, setSwipeOffsets] = useState<Record<string, number>>({});
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const touchStartX = useRef<Record<string, number>>({});

  useEffect(() => {
    setThemeTransient('light');
  }, [setThemeTransient]);

  useEffect(() => {
    if (user) {
      checkAccountStatus();
      fetchConsultantProfile();
      fetchActiveAssignment();
      fetchOpportunitiesCount();
      fetchAssignmentsCount();
      fetchRecentActivity();
      fetchAdminMessages();
    }
    fetchLogo();
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`dashboard-profile-${user.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'consultant_profiles',
        filter: `id=eq.${user.id}`,
      }, () => { fetchConsultantProfile(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id]);

  const checkAccountStatus = async () => {
    if (!user?.id) return;

    try {
      const { data: accountStatus } = await supabase
        .from('consultant_account_status')
        .select('status, paused_reason')
        .eq('consultant_id', user.id)
        .maybeSingle();

      if (accountStatus && accountStatus.status === 'paused') {
        await signOut();
        navigate('/consultant/login', {
          state: {
            error: `Your account has been paused. ${accountStatus.paused_reason || 'Please contact support for assistance.'}`
          }
        });
      }
    } catch (error) {
      console.error('Error checking account status:', error);
    }
  };

  const fetchLogo = async () => {
    try {
      setIsLoadingLogo(true);
      const defaultLogo = 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format';
      setLogoUrl(defaultLogo);

      try {
        const { data, error } = await supabase
          .from('settings')
          .select('value')
          .eq('key', 'logo_url')
          .single();

        if (error) {
          console.warn('Could not fetch logo from settings:', error.message);
          setIsLoadingLogo(false);
          return;
        }

        if (data?.value) {
          const img = new Image();
          img.src = data.value;

          img.onload = () => {
            setLogoUrl(data.value);
            setIsLoadingLogo(false);
          };

          img.onerror = () => {
            console.warn('Failed to load custom logo, using default');
            setIsLoadingLogo(false);
          };
        } else {
          setIsLoadingLogo(false);
        }
      } catch (fetchError) {
        console.warn('Network error fetching logo, using default:', fetchError);
        setIsLoadingLogo(false);
      }
    } catch (err) {
      console.error('Error fetching logo:', err);
      setIsLoadingLogo(false);
    }
  };

  const fetchConsultantProfile = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('consultant_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;
      setConsultantProfile(data);

      if (data) {
        const fields = [
          data.first_name,
          data.last_name,
          data.email,
          data.phone,
          data.title,
          data.country_region,
          data.experience_years,
          data.workday_modules,
          data.experience_text,
          data.resume_url,
          data.photo_url
        ];
        const filledFields = fields.filter(field => field && field.toString().trim() !== '').length;
        const completeness = Math.round((filledFields / fields.length) * 100);
        setProfileCompleteness(completeness);
      }
    } catch (error) {
      console.error('Error fetching consultant profile:', error);
    }
  };

  const fetchActiveAssignment = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('consultant_assignments')
        .select(`
          *,
          workday_client_profiles!consultant_assignments_client_id_fkey (
            full_name,
            email
          ),
          companies (
            name
          )
        `)
        .eq('consultant_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      setActiveAssignment(data);
    } catch (error) {
      console.error('Error fetching active assignment:', error);
    }
  };

  const fetchOpportunitiesCount = async () => {
    try {
      const { count, error } = await supabase
        .from('workday_client_job_postings')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'open');

      if (error) throw error;
      setOpportunitiesCount(count || 0);
    } catch (error) {
      console.error('Error fetching opportunities count:', error);
    }
  };

  const fetchAssignmentsCount = async () => {
    if (!user?.id) return;

    try {
      const { count, error } = await supabase
        .from('consultant_assignments')
        .select('*', { count: 'exact', head: true })
        .eq('consultant_id', user.id)
        .in('status', ['active', 'upcoming']);

      if (error) throw error;
      setAssignmentsCount(count || 0);
    } catch (error) {
      console.error('Error fetching assignments count:', error);
    }
  };

  const fetchRecentActivity = async () => {
    if (!user?.id) return;

    try {
      const activities: any[] = [];

      const { data: jobPostings } = await supabase
        .from('workday_client_job_postings')
        .select('id, job_title, created_at')
        .eq('status', 'open')
        .order('created_at', { ascending: false })
        .limit(2);

      if (jobPostings) {
        jobPostings.forEach(job => {
          activities.push({
            id: `job-${job.id}`,
            type: 'job_posting',
            title: `New opportunity: ${job.job_title}`,
            timestamp: job.created_at,
            color: 'bg-green-500'
          });
        });
      }

      const { data: applications } = await supabase
        .from('job_applications')
        .select(`
          id,
          created_at,
          status,
          workday_client_job_postings (
            job_title
          )
        `)
        .eq('consultant_id', user.id)
        .order('created_at', { ascending: false })
        .limit(2);

      if (applications) {
        applications.forEach((app: any) => {
          const jobTitle = app.workday_client_job_postings?.job_title || 'Unknown position';
          activities.push({
            id: `app-${app.id}`,
            type: 'application',
            title: `Application ${app.status} for ${jobTitle}`,
            timestamp: app.created_at,
            color: app.status === 'accepted' ? 'bg-green-500' : app.status === 'rejected' ? 'bg-red-500' : 'bg-blue-500'
          });
        });
      }

      const { data: timesheets } = await supabase
        .from('timesheets')
        .select('id, created_at, status, hours')
        .eq('consultant_id', user.id)
        .order('created_at', { ascending: false })
        .limit(2);

      if (timesheets) {
        timesheets.forEach(timesheet => {
          activities.push({
            id: `timesheet-${timesheet.id}`,
            type: 'timesheet',
            title: `Timesheet ${timesheet.status} - ${timesheet.hours} hours`,
            timestamp: timesheet.created_at,
            color: timesheet.status === 'approved' ? 'bg-green-500' : timesheet.status === 'rejected' ? 'bg-red-500' : 'bg-yellow-500'
          });
        });
      }

      const { data: profileUpdates } = await supabase
        .from('consultant_profiles')
        .select('id, updated_at')
        .eq('id', user.id)
        .maybeSingle();

      if (profileUpdates && profileUpdates.updated_at) {
        activities.push({
          id: 'profile-update',
          type: 'profile',
          title: 'Profile updated successfully',
          timestamp: profileUpdates.updated_at,
          color: 'bg-blue-500'
        });
      }

      activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setRecentActivity(activities.slice(0, 5));
    } catch (error) {
      console.error('Error fetching recent activity:', error);
    }
  };

  const fetchAdminMessages = async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase
        .from('consultant_messages')
        .select('id, subject, message, read_by_consultant, created_at')
        .eq('consultant_id', user.id)
        .eq('is_from_admin', true)
        .eq('consultant_deleted', false)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setAdminMessages(data || []);
    } catch (error) {
      console.error('Error fetching admin messages:', error);
    }
  };

  const markMessageRead = async (id: string) => {
    await supabase
      .from('consultant_messages')
      .update({ read_by_consultant: true })
      .eq('id', id);
    setAdminMessages(prev => prev.map(m => m.id === id ? { ...m, read_by_consultant: true } : m));
  };

  const deleteMessage = async (id: string) => {
    setDeletingId(id);
    await supabase
      .from('consultant_messages')
      .update({ consultant_deleted: true })
      .eq('id', id);
    setAdminMessages(prev => prev.filter(m => m.id !== id));
    setSwipeOffsets(prev => { const n = { ...prev }; delete n[id]; return n; });
    setDeletingId(null);
  };

  const handleTouchStart = (id: string, e: React.TouchEvent) => {
    touchStartX.current[id] = e.touches[0].clientX;
  };

  const handleTouchMove = (id: string, e: React.TouchEvent) => {
    const delta = e.touches[0].clientX - (touchStartX.current[id] ?? e.touches[0].clientX);
    if (delta < 0) {
      setSwipeOffsets(prev => ({ ...prev, [id]: Math.max(delta, -80) }));
    }
  };

  const handleTouchEnd = (id: string) => {
    const offset = swipeOffsets[id] ?? 0;
    if (offset < -50) {
      setSwipeOffsets(prev => ({ ...prev, [id]: -80 }));
    } else {
      setSwipeOffsets(prev => ({ ...prev, [id]: 0 }));
    }
  };

  const handleMouseDown = (id: string, e: React.MouseEvent) => {
    touchStartX.current[id] = e.clientX;
    const onMove = (ev: MouseEvent) => {
      const delta = ev.clientX - (touchStartX.current[id] ?? ev.clientX);
      if (delta < 0) {
        setSwipeOffsets(prev => ({ ...prev, [id]: Math.max(delta, -80) }));
      }
    };
    const onUp = () => {
      const offset = swipeOffsets[id] ?? 0;
      if (offset < -50) {
        setSwipeOffsets(prev => ({ ...prev, [id]: -80 }));
      } else {
        setSwipeOffsets(prev => ({ ...prev, [id]: 0 }));
      }
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const getTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const dashboardCards: DashboardCard[] = [
    {
      id: 'opportunities',
      title: 'Workday Opportunities',
      icon: <Briefcase className="w-8 h-8" />,
      description: 'Browse available Workday projects and positions',
      count: opportunitiesCount,
      href: '/consultant/opportunities'
    },
    {
      id: 'profile',
      title: 'Your Profile',
      icon: <User className="w-8 h-8" />,
      description: 'Manage your skills, experience, and availability',
      status: `${profileCompleteness}% Complete`,
      href: '/consultant/profile'
    },
    {
      id: 'assignments',
      title: 'Assignments',
      icon: <Calendar className="w-8 h-8" />,
      description: 'View current and upcoming project assignments',
      count: assignmentsCount,
      href: '/consultant/assignments'
    },
    {
      id: 'timesheet',
      title: 'Submit Time',
      icon: <Clock className="w-8 h-8" />,
      description: 'Log hours and submit timesheets for approval',
      status: activeAssignment ? 'Active Project' : 'No Active Projects',
      href: '/consultant/timesheet'
    },
    {
      id: 'ai',
      title: 'AI Assistant',
      icon: <Bot className="w-8 h-8" />,
      description: 'Get AI-powered insights and project assistance',
      href: '/consultant/ai'
    },
    {
      id: 'resources',
      title: 'Onboarding, Resources & Policies',
      icon: <BookOpen className="w-8 h-8" />,
      description: 'Access training materials, policies, and documentation',
      href: '/consultant/resources'
    },
  ];

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/consultant/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-200 ${
      theme === 'dark'
        ? 'bg-gray-900 text-white'
        : 'bg-gray-50 text-gray-900'
    }`}>
      {/* Black Header */}
      <nav className="fixed w-full z-50 bg-black backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex-1 flex items-center justify-between">
              <div className="text-white -mt-4 -mb-4 cursor-default select-none">
                {isLoadingLogo ? (
                  <div className="h-32 md:h-28 w-[200px] flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                  </div>
                ) : logoUrl ? (
                  <>
                    <img
                      src="/1.png"
                      alt="Perplexity"
                      className="h-40 w-auto object-contain"
                      onError={(e) => {
                        console.warn('Error loading logo image, falling back to text');
                        e.currentTarget.style.display = 'none';
                        const textFallback = e.currentTarget.nextElementSibling as HTMLElement;
                        if (textFallback) {
                          textFallback.style.display = 'block';
                        }
                      }}
                    />
                    <div
                      className="h-32 md:h-28 flex items-center justify-center text-primary text-2xl font-bold"
                      style={{ display: 'none' }}
                    >
                      PERPLEXITY
                    </div>
                  </>
                ) : (
                  <div className="h-32 md:h-28 w-[200px] flex items-center justify-center text-primary">
                    <ImageIcon size={32} />
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-4">
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-lg transition-colors text-white hover:bg-white/10"
                >
                  {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>

                <div className="text-white">
                  <NotificationBell />
                </div>

                <button
                  onClick={() => navigate('/consultant/settings')}
                  className="p-2 rounded-lg transition-colors text-white hover:bg-white/10"
                >
                  <Settings className="w-5 h-5" />
                </button>

                <button
                  onClick={handleLogout}
                  className="flex items-center px-4 py-2 rounded-lg transition-colors text-white hover:bg-white/10"
                >
                  <LogOut className="w-5 h-5 mr-2" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Dashboard content */}
      <main className="pt-32 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">
              Welcome back, {consultantProfile?.first_name || 'Consultant'}!
            </h1>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Here's what's happening with your Workday consulting projects.
            </p>
          </div>

          {/* Active Assignment Banner */}
          {activeAssignment && (
            <div className="mb-8">
              <div className={`p-6 rounded-xl border-2 ${
                theme === 'dark'
                  ? 'bg-blue-900/20 border-blue-500/50'
                  : 'bg-blue-50 border-blue-200 shadow-lg'
              }`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Briefcase className="w-6 h-6 text-blue-600" />
                      <h2 className="text-xl font-bold text-blue-900 dark:text-blue-100">
                        Currently On Assignment
                      </h2>
                      <span className="px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-full">
                        Active
                      </span>
                    </div>
                    <div className="mt-3 space-y-2">
                      {activeAssignment.companies && (
                        <p className="text-lg font-semibold text-blue-800 dark:text-blue-200">
                          Company: {activeAssignment.companies.name}
                        </p>
                      )}
                      {activeAssignment.workday_client_profiles && (
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          Client Contact: {activeAssignment.workday_client_profiles.full_name}
                        </p>
                      )}
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        Start Date: {new Date(activeAssignment.start_date).toLocaleDateString()}
                      </p>
                      {activeAssignment.hourly_rate && (
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          Rate: ${activeAssignment.hourly_rate}/hr
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Workday Community Featured Section */}
          <div className="mb-8">
            <Link
              to="/consultant/community"
              className={`block p-8 rounded-xl transition-all duration-200 hover:scale-[1.02] ${
                theme === 'dark'
                  ? 'bg-gradient-to-r from-[#40E0D0]/20 to-[#0066FF]/20 hover:from-[#40E0D0]/30 hover:to-[#0066FF]/30 border-2 border-[#40E0D0]/50'
                  : 'bg-gradient-to-r from-[#40E0D0]/10 to-[#0066FF]/10 hover:from-[#40E0D0]/20 hover:to-[#0066FF]/20 border-2 border-[#40E0D0] shadow-lg hover:shadow-xl'
              }`}
            >
              <div className="flex items-start space-x-6">
                <div className={`p-4 rounded-xl ${
                  theme === 'dark' ? 'bg-[#40E0D0]/20' : 'bg-[#40E0D0]/30'
                }`}>
                  <MessageSquare className="w-12 h-12 text-[#40E0D0]" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold mb-3">
                    Perplexity's Workday Community
                  </h2>
                  <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Stay updated with opportunities, news, events, and discussions from the Workday community
                  </p>
                  <div className="mt-4">
                    <span className="inline-flex items-center px-4 py-2 bg-[#40E0D0] text-black font-semibold rounded-lg hover:bg-[#40E0D0]/90 transition-colors">
                      Visit Community
                      <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* Dashboard cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dashboardCards.map((card) => (
              <Link
                key={card.id}
                to={card.href}
                className={`block p-6 rounded-xl transition-all duration-200 hover:scale-105 ${
                  theme === 'dark'
                    ? 'bg-gray-800 hover:bg-gray-750 border border-gray-700'
                    : 'bg-white hover:bg-gray-50 border border-gray-200 shadow-sm hover:shadow-md'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg ${
                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                  }`}>
                    <div className="text-primary">{card.icon}</div>
                  </div>
                  {card.count && (
                    <span className="bg-primary text-black px-2 py-1 rounded-full text-sm font-semibold">
                      {card.count}
                    </span>
                  )}
                  {card.status && (
                    <span className={`px-2 py-1 rounded-full text-sm font-semibold ${
                      card.status === 'Due Today'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {card.status}
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-semibold mb-2">{card.title}</h3>
                <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  {card.description}
                </p>
              </Link>
            ))}
          </div>

          {/* Messages from Perplexity Team */}
          <div className="mt-12">
            <div className={`rounded-xl border overflow-hidden ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>

              {/* Header — clicking anywhere toggles the list */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => setMessagesVisible(v => { const next = !v; try { localStorage.setItem('consultant_msgs_visible', String(next)); } catch {} return next; })}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setMessagesVisible(v => { const next = !v; try { localStorage.setItem('consultant_msgs_visible', String(next)); } catch {} return next; }); }}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                className={`flex items-center justify-between px-6 py-5 transition-colors ${theme === 'dark' ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#40E0D0]/20">
                    <Mail className="w-5 h-5 text-[#40E0D0]" />
                  </div>
                  <div className="text-left">
                    <h2 className="text-lg font-bold">Messages from Perplexity Team</h2>
                    <p className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      {messagesVisible ? 'Click to hide messages' : 'Click to show messages'}
                      {adminMessages.filter(m => !m.read_by_consultant).length > 0 && (
                        <span className="ml-2 text-[#40E0D0] font-semibold">
                          · {adminMessages.filter(m => !m.read_by_consultant).length} unread
                        </span>
                      )}
                    </p>
                  </div>
                  {adminMessages.filter(m => !m.read_by_consultant).length > 0 && (
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#40E0D0] text-black text-[10px] font-bold ml-1">
                      {adminMessages.filter(m => !m.read_by_consultant).length}
                    </span>
                  )}
                </div>
                <div className={`flex items-center gap-2 text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span>{messagesVisible ? 'Hide' : 'Show'}</span>
                  {messagesVisible ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </div>

              {/* Collapsible message list */}
              {messagesVisible && (
                <div className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`} style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {adminMessages.length === 0 ? (
                    <div className="py-12 text-center">
                      <Mail className={`w-10 h-10 mx-auto mb-3 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'}`} />
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>No messages from the Perplexity Team yet</p>
                    </div>
                  ) : (
                    <div className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-100'}`}>
                      {adminMessages.map(msg => {
                        const offset = swipeOffsets[msg.id] ?? 0;
                        const isRevealed = offset <= -60;
                        const isExpanded = expandedMessageId === msg.id;
                        return (
                          <div key={msg.id} className="relative overflow-hidden">
                            {/* Delete zone — always behind, revealed by swipe */}
                            <div
                              className="absolute right-0 top-0 bottom-0 w-20 flex items-center justify-center bg-red-500"
                            >
                              <button
                                onClick={(e) => { e.stopPropagation(); deleteMessage(msg.id); }}
                                disabled={deletingId === msg.id}
                                className="flex flex-col items-center gap-1 text-white"
                              >
                                <Trash2 className="w-5 h-5" />
                                <span className="text-[10px] font-bold">Delete</span>
                              </button>
                            </div>

                            {/* Message card — slides left on swipe to reveal delete */}
                            <div
                              className={`relative transition-transform duration-150 ${
                                !msg.read_by_consultant
                                  ? theme === 'dark' ? 'bg-gray-800' : 'bg-teal-50/50'
                                  : theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                              }`}
                              style={{ transform: `translateX(${offset}px)` }}
                              onTouchStart={e => handleTouchStart(msg.id, e)}
                              onTouchMove={e => handleTouchMove(msg.id, e)}
                              onTouchEnd={() => handleTouchEnd(msg.id)}
                            >
                              {/* Row header — click to expand/collapse this message */}
                              <div
                                className="flex items-center gap-4 px-6 py-4 cursor-pointer"
                                onClick={() => {
                                  if (isRevealed) { setSwipeOffsets(prev => ({ ...prev, [msg.id]: 0 })); return; }
                                  if (!msg.read_by_consultant) markMessageRead(msg.id);
                                  setExpandedMessageId(isExpanded ? null : msg.id);
                                }}
                              >
                                {/* Unread indicator dot */}
                                <div className="flex-shrink-0">
                                  {!msg.read_by_consultant
                                    ? <div className="w-2.5 h-2.5 rounded-full bg-[#40E0D0]" />
                                    : <div className="w-2.5 h-2.5 rounded-full bg-transparent" />
                                  }
                                </div>

                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2">
                                    <p className={`text-sm font-semibold truncate ${!msg.read_by_consultant ? (theme === 'dark' ? 'text-white' : 'text-gray-900') : (theme === 'dark' ? 'text-gray-300' : 'text-gray-600')}`}>
                                      {msg.subject}
                                    </p>
                                    <span className={`text-xs flex-shrink-0 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                                      {getTimeAgo(msg.created_at)}
                                    </span>
                                  </div>
                                  {!isExpanded && (
                                    <p className={`text-sm truncate mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                      {msg.message}
                                    </p>
                                  )}
                                </div>

                                {/* Expand/collapse chevron + visible delete button */}
                                <div className="flex items-center gap-3 flex-shrink-0">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); deleteMessage(msg.id); }}
                                    disabled={deletingId === msg.id}
                                    className={`p-1.5 rounded-lg transition-colors ${theme === 'dark' ? 'text-gray-500 hover:text-red-400 hover:bg-red-900/30' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'}`}
                                    title="Delete message"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                  {isExpanded
                                    ? <ChevronUp className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}`} />
                                    : <ChevronDown className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}`} />
                                  }
                                </div>
                              </div>

                              {/* Expanded full message body */}
                              {isExpanded && (
                                <div className={`px-6 pb-5 pt-1`}>
                                  <div className={`rounded-lg p-4 text-sm leading-relaxed whitespace-pre-wrap ${theme === 'dark' ? 'bg-gray-700/60 text-gray-200' : 'bg-gray-50 text-gray-700'}`}>
                                    {msg.message}
                                  </div>
                                  <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                                    Received {new Date(msg.created_at).toLocaleString()}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Recent activity section */}
          <div className="mt-8">
            <h2 className="text-2xl font-bold mb-6">Recent Activity</h2>
            <div className={`rounded-xl p-6 ${
              theme === 'dark'
                ? 'bg-gray-800 border border-gray-700'
                : 'bg-white border border-gray-200 shadow-sm'
            }`}>
              {recentActivity.length > 0 ? (
                <div className="space-y-4">
                  {recentActivity.map((activity) => (
                    <div key={activity.id} className="flex items-center space-x-4">
                      <div className={`w-2 h-2 ${activity.color} rounded-full`}></div>
                      <div>
                        <p className="font-medium">{activity.title}</p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          {getTimeAgo(activity.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock className={`w-12 h-12 mx-auto mb-3 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
                  <p className={`text-lg font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    No recent activity
                  </p>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                    Start exploring opportunities to see activity here
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}